import { useEffect, useRef } from 'react';

const ACTION = 'PLPGridHealth';
const RECOVERED_ACTION = 'PLPGridRecovered';

const CHECK_MS = 10000;
const RECOVERY_MS = 25000;
const SAMPLE_RENDERED = 0.05;
const POD_SELECTOR = 'section[id^="product-results-pods-"] > :not([data-testid^="slot-"])';

// "0121" and "121" should compare as the same store
const normalizeStoreId = (id) => (id === null || id === undefined || id === '' ? null : String(Number(id)));

const SEARCH_MODEL_PREFIX = 'searchModel(';
const MAX_SSR_DEPTH = 3;

// Reads storeId from a searchModel cache key
const parseStoreIdFromKey = (key) => {
  if (!key.startsWith(SEARCH_MODEL_PREFIX) || !key.endsWith(')')) {
    return null;
  }
  try {
    const { storeId } = JSON.parse(key.slice(SEARCH_MODEL_PREFIX.length, -1));
    return storeId === null || storeId === undefined ? null : String(storeId);
  } catch (err) {
    if (err instanceof SyntaxError) {
      return null;
    }
    throw err;
  }
};

const findSsrStoreId = (node, depth = 0) => {
  if (!node || typeof node !== 'object' || depth > MAX_SSR_DEPTH) {
    return null;
  }
  let found = null;
  Object.keys(node).some((key) => {
    found = parseStoreIdFromKey(key) || findSsrStoreId(node[key], depth + 1);
    return found !== null;
  });
  return found;
};

// Store the server rendered with, read from the SSR cache key
const getSsrStoreId = () => {
  try {
    return findSsrStoreId(window.__APOLLO_STATE__);
  } catch (err) {
    return null;
  }
};

// How many searchModel requests have completed (pending requests excluded)
const countSearchModelRequests = () => {
  try {
    return window.performance.getEntriesByType('resource')
      .filter((entry) => entry.name.includes('opname=searchModel')).length;
  } catch (err) {
    return -1;
  }
};

// Returns how the page was loaded (initial load only)
const getNavigationType = () => {
  try {
    return window.performance.getEntriesByType('navigation')[0]?.type || 'unknown';
  } catch (err) {
    return 'unknown';
  }
};

// Returns whether plp-grid exists, is empty, and how many pods it has.
const readGrid = () => {
  const grid = document.getElementById('plp-grid');
  if (!grid) {
    return { found: false, empty: false, podCount: 0 };
  }
  const podCount = grid.querySelectorAll(POD_SELECTOR).length;
  return {
    found: true,
    empty: podCount === 0,
    podCount
  };
};

const sendPageAction = (name, attributes) => {
  const { newrelic } = window;
  if (typeof newrelic?.addPageAction === 'function') {
    newrelic.addPageAction(name, attributes);
  }
};

/**
 * Classifies why the grid is empty:
 * - localizedFirstRenderCacheMiss: store localized on first render, unchanged, differs from SSR store
 * - neverLocalized: store never localized
 * - storeChangedStillEmpty: store changed but grid still empty
 */
export const getPathway = ({ first, current, ssrStoreId }) => {
  const storeChanged = normalizeStoreId(current.storeId) !== normalizeStoreId(first.storeId);
  if (first.isLocalized && !storeChanged && ssrStoreId
        && normalizeStoreId(ssrStoreId) !== normalizeStoreId(first.storeId)) {
    return 'localizedFirstRenderCacheMiss';
  }
  if (!current.isLocalized) {
    return 'neverLocalized';
  }
  if (storeChanged) {
    return 'storeChangedStillEmpty';
  }
  return 'other';
};

// Labels the grid state and, for grids that didn't load, the most likely cause
const identifyGrid = ({ grid, first, current, ssrStoreId }) => {
  if (!grid.found) {
    return { gridState: 'missing', pathway: 'gridMissing' };
  }
  if (grid.empty) {
    return { gridState: 'empty', pathway: getPathway({ first, current, ssrStoreId }) };
  }
  return { gridState: 'rendered', pathway: 'n/a' };
};

// Builds the PLPGridHealth event attributes
const buildHealthAttributes = ({ grid, latest, first, notLoaded }) => {
  const current = latest.store || {};
  const ssrStoreId = getSsrStoreId();
  const { gridState, pathway } = identifyGrid({ grid, first, current, ssrStoreId });
  return {
    gridState,
    sampleRate: notLoaded ? 1 : SAMPLE_RENDERED,
    pathway,
    podCount: grid.podCount,
    resultCount: latest.resultCount ?? null,
    resultsLoading: latest.resultsLoading ?? null,
    firstStoreId: first.storeId,
    firstIsLocalized: first.isLocalized,
    currentStoreId: current.storeId ?? null,
    currentIsLocalized: !!current.isLocalized,
    storeChanged: normalizeStoreId(current.storeId) !== normalizeStoreId(first.storeId),
    ssrStoreId,
    searchModelRequestsCompleted: countSearchModelRequests(),
    navigationType: getNavigationType(),
    pageType: latest.pageType,
    channel: latest.channel,
    experienceType: latest.experienceType || 'default',
    customerType: latest.customerType || 'unknown',
    isConsumerApp: !!latest.isConsumerApp,
    experienceName: window.experienceMetadata?.name || null
  };
};

// First check: reports the grid's health; returns true if the grid didn't load (so recovery is checked)
const runFirstCheck = (latest, first) => {
  const grid = readGrid();
  const notLoaded = !grid.found || grid.empty;
  // A loaded search or category with zero results is expected to have no pods
  const isNoResultsPage = latest.resultsLoading === false && latest.resultCount === 0;
  if (grid.found && grid.empty && isNoResultsPage) {
    return false;
  }
  if (!notLoaded && Math.random() > SAMPLE_RENDERED) {
    return false;
  }
  sendPageAction(ACTION, buildHealthAttributes({ grid, latest, first, notLoaded }));
  return notLoaded;
};

// Recovery check: reports if a grid that didn't load now has products
const runRecoveryCheck = (latest) => {
  const grid = readGrid();
  if (grid.found && grid.podCount > 0) {
    sendPageAction(RECOVERED_ACTION, {
      podCount: grid.podCount,
      recoveredBySeconds: RECOVERY_MS / 1000,
      pageType: latest.pageType,
      channel: latest.channel
    });
  }
};

/**
 * Reports to New Relic whether the PLP grid rendered products, and if not,
 * which pathway most likely caused the empty grid.
 */
export const usePlpGridHealthTracker = ({
  enabled,
  store,
  resultCount,
  resultsLoading,
  pageType,
  channel,
  experienceType,
  customerType,
  isConsumerApp
}) => {
  const currentPropsRef = useRef({});
  currentPropsRef.current = {
    store,
    resultCount,
    resultsLoading,
    pageType,
    channel,
    experienceType,
    customerType,
    isConsumerApp
  };

  // Snapshot of the store on the first client render (used to detect a store that was already localized)
  const firstRef = useRef(null);
  if (firstRef.current === null && typeof window !== 'undefined') {
    firstRef.current = {
      storeId: store?.storeId ?? null,
      isLocalized: !!store?.isLocalized
    };
  }

  // Checks the grid after 10s and reports empty or missing grids (and a sample of healthy ones) to New Relic.
  // If it didn't load, checks again at 25s to report whether it recovered.
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }
    let gridNotLoaded = false;

    const firstCheck = setTimeout(() => {
      gridNotLoaded = runFirstCheck(currentPropsRef.current, firstRef.current);
    }, CHECK_MS);

    const recoveryCheck = setTimeout(() => {
      if (gridNotLoaded) {
        runRecoveryCheck(currentPropsRef.current);
      }
    }, RECOVERY_MS);

    return () => {
      clearTimeout(firstCheck);
      clearTimeout(recoveryCheck);
    };
  }, [enabled]);
};